<template>
  <v-row>
    <!-- ── Header Actions ── -->
    <v-col cols="12" class="d-flex justify-end align-center flex-wrap pb-0 mb-2" style="gap:8px">
      <v-btn small elevation="2" class="text-caption font-weight-bold text-none" color="info" rounded
        @click="shareSelectedProducts" :disabled="selectedProducts.length === 0">
        <v-icon left size="16">mdi-facebook</v-icon>
        Share{{ selectedProducts.length > 0 ? ` (${selectedProducts.length})` : '' }}
      </v-btn>
      <v-btn small elevation="2" class="text-caption font-weight-bold text-none" color="teal darken-1" rounded
        :to="{ name: 'marketplace.add_category' }">
        <v-icon left size="16">mdi-tag-plus</v-icon>
        <span class="d-none d-sm-inline">Add Category</span>
        <span class="d-sm-none">Category</span>
      </v-btn>
      <v-btn small elevation="2" class="text-caption font-weight-bold text-none" color="primary darken-1" rounded
        :to="{ name: 'marketplace.add_products' }">
        <v-icon left size="16">mdi-plus</v-icon>
        <span class="d-none d-sm-inline">Add Product</span>
        <span class="d-sm-none">Product</span>
      </v-btn>
    </v-col>

    <!-- ── Main Card ── -->
    <v-col cols="12">
      <v-card elevation="3" class="rounded-xl overflow-hidden">

        <!-- Filters Toolbar -->
        <v-sheet color="grey lighten-4" class="pa-3">
          <v-row align="center" dense>
            <v-col cols="12" sm="4">
              <v-select
                v-model="selectedCategoryFilter"
                ref="selectedCategoryFilter"
                outlined dense hide-details rounded filled
                background-color="white"
                item-value="id" item-text="name"
                :items="category_types"
                label="Filter by Category"
                prepend-inner-icon="mdi-filter-variant"
                :loading="categoriesLoading"
                :no-data-text="categoriesLoading ? 'Loading…' : 'No categories found'"
              ></v-select>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="selectedStartDate"
                dense hide-details outlined rounded filled
                background-color="white" type="date" label="From"
                prepend-inner-icon="mdi-calendar-start"
              ></v-text-field>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="selectedEndDate"
                dense hide-details outlined rounded filled
                background-color="white" type="date" label="To"
                prepend-inner-icon="mdi-calendar-end"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex justify-end">
              <v-btn v-if="!loading" color="primary" elevation="2" rounded small block
                @click="getProducts()" class="text-none font-weight-bold">
                <v-icon left size="18">mdi-magnify</v-icon>Search
              </v-btn>
              <div v-else class="d-flex justify-center align-center" style="width:100%">
                <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
              </div>
            </v-col>
          </v-row>
        </v-sheet>

        <!-- Table Header / Search -->
        <v-card-title class="px-4 py-3 flex-wrap" style="gap:8px">
          <div class="d-flex align-center">
            <v-icon color="primary" class="me-2">mdi-package-variant-closed</v-icon>
            <span class="text-h6 font-weight-bold">Products</span>
            <v-chip class="ms-3 font-weight-bold" color="primary" text-color="white" x-small pill>
              {{ requestedProducts.length }}
            </v-chip>
          </div>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search products…"
            single-line hide-details dense filled rounded
            style="max-width:260px; min-width:150px; flex:1 1 150px;"
          ></v-text-field>
        </v-card-title>

        <!-- ══════════════════════════════════════════════
             MOBILE VIEW  (xs only)  — card list
             ══════════════════════════════════════════ -->
        <div v-if="$vuetify.breakpoint.xsOnly" class="px-3 pb-3">

          <!-- Select-all bar -->
          <div class="d-flex align-center mb-2 px-1">
            <v-checkbox
              :input-value="allSelected"
              :indeterminate="someSelected"
              dense hide-details
              class="mt-0 pt-0"
              @change="toggleSelectAll"
            ></v-checkbox>
            <span class="text-caption grey--text">
              {{ selectedProducts.length > 0 ? `${selectedProducts.length} selected` : 'Select all' }}
            </span>
          </div>

          <!-- Empty state -->
          <div v-if="filteredMobile.length === 0" class="pa-8 text-center">
            <v-icon size="48" color="grey lighten-2">mdi-package-variant</v-icon>
            <p class="text-subtitle-1 grey--text mt-2">No products found</p>
            <v-btn small color="primary" text @click="getProducts" class="text-none">Refresh</v-btn>
          </div>

          <!-- Product cards -->
          <v-card
            v-for="item in filteredMobile"
            :key="item.id"
            outlined
            class="mb-3 rounded-xl overflow-hidden product-mobile-card"
            :class="{ 'selected-card': isSelected(item) }"
          >
            <!-- Card top row: checkbox + image + name + price -->
            <div class="d-flex align-start pa-3" style="gap:10px">
              <v-checkbox
                :input-value="isSelected(item)"
                dense hide-details
                class="mt-0 pt-0 flex-shrink-0"
                @change="toggleItem(item)"
              ></v-checkbox>

              <v-avatar size="56" rounded="lg" class="flex-shrink-0">
                <v-img :src="item.get_image" contain></v-img>
              </v-avatar>

              <div class="flex-grow-1" style="min-width:0">
                <router-link
                  :to="'/market_place/products/detail/' + item.id"
                  class="font-weight-bold text--primary text-decoration-none hover-primary d-block text-truncate"
                >{{ item.name }}</router-link>

                <div class="mt-1 d-flex align-center flex-wrap" style="gap:6px">
                  <span class="font-weight-bold primary--text text-body-2">{{ formatPrice(item.price) }}</span>
                  <span class="text-caption grey--text">Buy: {{ formatPrice(item.wholesale_price) }}</span>
                </div>

                <div class="mt-1 d-flex align-center flex-wrap" style="gap:6px">
                  <v-chip x-small :color="item.quantity > 0 ? 'success' : 'error'" text-color="white" pill class="font-weight-bold">
                    Stock: {{ item.quantity }}
                  </v-chip>
                  <v-chip x-small color="blue-grey lighten-4" text-color="blue-grey darken-2" pill>
                    Sold: {{ item.no_bought || 0 }}
                  </v-chip>
                </div>

                <div class="text-caption grey--text mt-1">{{ item.get_date_str }}</div>
              </div>
            </div>

            <!-- Card actions row -->
            <v-divider></v-divider>
            <div class="d-flex align-center justify-space-between px-3 py-2">
              <v-btn icon small color="success"
                :href="'https://api.whatsapp.com/send?phone=' + item.supplier_contact"
                target="_blank">
                <v-icon size="18">mdi-whatsapp</v-icon>
              </v-btn>
              <v-btn icon small color="grey darken-1" @click="copyDescription(item.description)">
                <v-icon size="18">mdi-content-copy</v-icon>
              </v-btn>
              <v-btn icon small color="primary" @click="shareSingleProduct(item)">
                <v-icon size="18">mdi-facebook</v-icon>
              </v-btn>
              <v-btn icon small color="error" @click="confirmDeleteProduct(item)">
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </v-card>
        </div>

        <!-- ══════════════════════════════════════════════
             DESKTOP VIEW (sm+) — data table
             ══════════════════════════════════════════ -->
        <div v-else class="table-scroll-wrapper">
          <v-data-table
            v-model="selectedProducts"
            :headers="headers"
            :items="requestedProducts"
            item-key="id"
            :items-per-page="10"
            :search="search"
            show-select
            class="elevation-0"
            :header-props="{ class: 'text-uppercase text-caption font-weight-bold grey--text text--darken-1' }"
          >
            <template #[`item.name`]="{ item }">
              <div class="d-flex flex-column py-2" style="min-width:120px">
                <router-link
                  :to="'/market_place/products/detail/' + item.id"
                  class="font-weight-bold text--primary text-decoration-none hover-primary"
                >{{ item.name }}</router-link>
              </div>
            </template>

            <template #[`item.get_image`]="{ item }">
              <v-avatar size="44" rounded="lg" class="my-1">
                <v-img :src="item.get_image" contain></v-img>
              </v-avatar>
            </template>

            <template #[`item.price`]="{ item }">
              <span class="font-weight-bold primary--text text-no-wrap">{{ formatPrice(item.price) }}</span>
            </template>

            <template #[`item.wholesale_price`]="{ item }">
              <span class="text--secondary text-no-wrap">{{ formatPrice(item.wholesale_price) }}</span>
            </template>

            <template #[`item.quantity`]="{ item }">
              <v-chip x-small :color="item.quantity > 0 ? 'success' : 'error'" text-color="white" pill class="font-weight-bold">
                {{ item.quantity }}
              </v-chip>
            </template>

            <template #[`item.no_bought`]="{ item }">
              <span class="font-weight-medium">{{ item.no_bought || 0 }}</span>
            </template>

            <template #[`item.get_date_str`]="{ item }">
              <span class="text-caption text--secondary text-no-wrap">{{ item.get_date_str }}</span>
            </template>

            <template #[`item.supplier_contact`]="{ item }">
              <v-btn icon small color="success"
                :href="'https://api.whatsapp.com/send?phone=' + item.supplier_contact"
                target="_blank" class="hover-scale">
                <v-icon size="18">mdi-whatsapp</v-icon>
              </v-btn>
            </template>

            <template #[`item.actions`]="{ item }">
              <div class="d-flex justify-end align-center" style="min-width:100px">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="grey darken-1" v-bind="attrs" v-on="on"
                      class="hover-scale me-1" @click="copyDescription(item.description)">
                      <v-icon size="18">mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>Copy Description</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="primary" v-bind="attrs" v-on="on"
                      class="hover-scale me-1" @click="shareSingleProduct(item)">
                      <v-icon size="18">mdi-facebook</v-icon>
                    </v-btn>
                  </template>
                  <span>Share on Facebook</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="error" v-bind="attrs" v-on="on"
                      class="hover-scale" @click="confirmDeleteProduct(item)">
                      <v-icon size="18">mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete Product</span>
                </v-tooltip>
              </div>
            </template>

            <template #no-data>
              <div class="pa-8 text-center">
                <v-icon size="48" color="grey lighten-2">mdi-package-variant</v-icon>
                <p class="text-subtitle-1 grey--text mt-2">No products found</p>
                <v-btn small color="primary" text @click="getProducts" class="text-none">Refresh</v-btn>
              </div>
            </template>
          </v-data-table>
        </div>

      </v-card>
    </v-col>

    <!-- ── Delete Dialog ── -->
    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <v-card class="rounded-xl pa-2">
        <v-card-text class="text-center pt-6 pb-2">
          <v-avatar color="error lighten-5" size="64" class="mb-3">
            <v-icon color="error" size="32">mdi-trash-can-outline</v-icon>
          </v-avatar>
          <h3 class="text-h6 font-weight-bold mb-2">Delete Product?</h3>
          <p class="text-body-2 grey--text text--darken-1">
            Are you sure you want to delete
            <strong>"{{ productToDelete ? productToDelete.name : '' }}"</strong>?<br>
            This action cannot be undone.
          </p>
          <v-alert v-if="deleteError" type="error" dense text class="mt-3 text-left">{{ deleteError }}</v-alert>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn text rounded class="text-none font-weight-bold" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" rounded class="text-none font-weight-bold px-6"
            @click="deleteProduct" :loading="deleting" elevation="2">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Share Dialog ── -->
    <v-dialog v-model="shareDialog" max-width="560" persistent>
      <v-card class="rounded-xl overflow-hidden">
        <v-sheet color="primary" class="pa-4 d-flex align-center">
          <v-icon color="white" size="24" class="me-2">mdi-facebook</v-icon>
          <span class="text-h6 white--text font-weight-bold">Share on Facebook</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="shareDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-sheet>

        <v-card-text class="pa-0">
          <div v-if="shareLoading" class="text-center py-10">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            <p class="mt-4 text-body-1 grey--text">Preparing your share…</p>
          </div>

          <div v-else-if="shareData" class="pa-4">
            <v-alert type="info" dense text border="left" colored-border class="mb-4 rounded-lg">
              <div class="text-body-2">
                <strong>Share the backend URL</strong> so Facebook automatically fetches the image and description.
              </div>
            </v-alert>

            <h4 class="text-subtitle-2 font-weight-bold text-uppercase grey--text mb-3">Facebook Preview</h4>

            <div v-for="product in shareData" :key="product.id" class="mb-4 facebook-preview">
              <v-card outlined class="rounded-lg overflow-hidden" elevation="1">
                <div v-if="product.has_video" class="px-3 pt-3 pb-1">
                  <p class="text-caption font-weight-bold mb-1">Media type to share:</p>
                  <v-radio-group v-model="product.selectedMedia" row class="mt-0 mb-0">
                    <v-radio label="Image" value="image"></v-radio>
                    <v-radio label="Video" value="video"></v-radio>
                  </v-radio-group>
                </div>
                <v-img
                  v-if="product.selectedMedia === 'image' || !product.has_video"
                  :src="product.has_video ? product.video_thumbnail : product.image_url"
                  height="180" class="grey lighten-3" contain
                ></v-img>
                <div v-else>
                  <video :src="product.video_url" controls height="180"
                    style="width:100%;background:#000" preload="metadata"></video>
                </div>
                <v-card-text class="pa-3" style="background:#f0f2f5">
                  <div class="text-caption text-uppercase grey--text font-weight-bold mb-1">YOURSTORE.COM</div>
                  <div class="font-weight-bold text-body-1 mb-1 line-clamp-2">{{ product.name }}</div>
                  <div class="text-body-2 grey--text text--darken-1 line-clamp-2">
                    {{ product.description || 'No description available.' }}
                  </div>
                  <div class="mt-2 primary--text font-weight-bold text-body-1">{{ formatPrice(product.price) }}</div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0" v-if="!shareLoading && shareData">
          <v-spacer></v-spacer>
          <v-btn text rounded class="text-none font-weight-bold" @click="shareDialog = false">Cancel</v-btn>
          <v-btn color="primary" rounded class="text-none font-weight-bold px-6"
            @click="openFacebookShare" elevation="2">
            <v-icon left size="18">mdi-facebook</v-icon>Open Facebook
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import axios from 'axios'
import Spinner from "@/components/Spinner.vue"

export default {
  components: { Spinner },

  data() {
    return {
      headers: [
        { text: 'Product',    value: 'name',            width: '22%' },
        { text: 'Image',      value: 'get_image',       sortable: false, width: '7%' },
        { text: 'Sell Price', value: 'price',           width: '11%' },
        { text: 'Buy Price',  value: 'wholesale_price', width: '11%' },
        { text: 'Stock',      value: 'quantity',        width: '7%',  align: 'center' },
        { text: 'Sold',       value: 'no_bought',       width: '7%',  align: 'center' },
        { text: 'Date',       value: 'get_date_str',    width: '11%' },
        { text: 'Contact',    value: 'supplier_contact',sortable: false, align: 'center', width: '7%' },
        { text: 'Actions',    value: 'actions',         align: 'end', sortable: false,   width: '17%' },
      ],
      selectedCategoryFilter: '',
      category_types: [],
      categoriesLoading: false,
      requestedProducts: [],
      selectedProducts: [],
      search: '',
      selectedStartDate: null,
      selectedEndDate: null,
      loading: false,
      deleteDialog: false,
      productToDelete: null,
      deleting: false,
      deleteError: null,
      shareDialog: false,
      shareLoading: false,
      shareData: null,
      storageKey: 'marketplace_manager_products_state',
    }
  },

  computed: {
    // Mobile: filter by search term
    filteredMobile() {
      if (!this.search) return this.requestedProducts
      const q = this.search.toLowerCase()
      return this.requestedProducts.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.get_date_str || '').toLowerCase().includes(q)
      )
    },
    allSelected() {
      return this.requestedProducts.length > 0 &&
        this.selectedProducts.length === this.requestedProducts.length
    },
    someSelected() {
      return this.selectedProducts.length > 0 &&
        this.selectedProducts.length < this.requestedProducts.length
    },
  },

  mounted() {
    document.title = 'Manager: Products | List'
    this.loadFromStorage()
    this.getCategories()
  },

  methods: {
    // ── Mobile select helpers ────────────────────────────────────────────
    isSelected(item) {
      return this.selectedProducts.some(p => p.id === item.id)
    },
    toggleItem(item) {
      if (this.isSelected(item)) {
        this.selectedProducts = this.selectedProducts.filter(p => p.id !== item.id)
      } else {
        this.selectedProducts = [...this.selectedProducts, item]
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedProducts = []
      } else {
        this.selectedProducts = [...this.requestedProducts]
      }
    },

    // ── Local Storage ────────────────────────────────────────────────────
    saveToStorage() {
      const state = {
        selectedCategoryFilter: this.selectedCategoryFilter,
        selectedStartDate:      this.selectedStartDate,
        selectedEndDate:        this.selectedEndDate,
        requestedProducts:      this.requestedProducts,
        category_types:         this.category_types,
      }
      try { localStorage.setItem(this.storageKey, JSON.stringify(state)) } catch (_) {}
    },

    loadFromStorage() {
      const saved = localStorage.getItem(this.storageKey)
      if (!saved) return
      try {
        const state = JSON.parse(saved)
        if (state.selectedCategoryFilter) this.selectedCategoryFilter = state.selectedCategoryFilter
        if (state.selectedStartDate)      this.selectedStartDate      = state.selectedStartDate
        if (state.selectedEndDate)        this.selectedEndDate        = state.selectedEndDate
        if (Array.isArray(state.requestedProducts)) this.requestedProducts = state.requestedProducts
        if (Array.isArray(state.category_types) && state.category_types.length) {
          this.category_types = state.category_types
        }
      } catch (_) { localStorage.removeItem(this.storageKey) }
    },

    // ── Helpers ──────────────────────────────────────────────────────────
    formatPrice(price) {
      if (!price) return '0 XAF'
      return new Intl.NumberFormat('en-US').format(price) + ' XAF'
    },

    showSnackbar(message, color) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },

    handleError(error, defaultMessage) {
      if (error.response) {
        this.showSnackbar(error.response.status + ' ' + error.response.statusText, 'error')
      } else {
        this.showSnackbar(
          defaultMessage || 'An error occurred, please check your connection and try again.',
          'error'
        )
      }
    },

    async copyDescription(description) {
      if (!description || !description.trim()) {
        this.showSnackbar('No description available for this product', 'warning')
        return
      }
      try {
        const temp = document.createElement('div')
        temp.innerHTML = description
        temp.querySelectorAll('p, br').forEach(el => {
          el.replaceWith((el.textContent || '') + '\n')
        })
        const plainText = temp.innerText.replace(/\n{3,}/g, '\n\n').trim()
        await navigator.clipboard.writeText(plainText)
        this.showSnackbar('Description copied to clipboard!', 'success')
      } catch (_) {
        this.showSnackbar('Failed to copy description', 'error')
      }
    },

    // ── API ──────────────────────────────────────────────────────────────
    async getCategories() {
      this.categoriesLoading = true
      try {
        const response = await axios.get('/api/v1/manager/market_place/categories/')
        this.category_types = response.data
        this.saveToStorage()
      } catch (error) {
        this.handleError(error, 'Error loading categories')
      } finally {
        this.categoriesLoading = false
      }
    },

    async getProducts() {
      if (!this.selectedCategoryFilter) {
        this.showSnackbar('Please select a Category', 'red darken-4')
        this.$refs.selectedCategoryFilter.focus()
        return
      }
      const start = this.selectedStartDate || 'null'
      const end   = this.selectedEndDate   || 'null'
      this.loading = true
      try {
        const response = await axios.get(
          `/api/v1/manager/market_place/get/products/${this.selectedCategoryFilter}/${start}/${end}/`
        )
        this.requestedProducts = response.data
        this.selectedProducts  = []
        this.saveToStorage()
      } catch (error) {
        this.handleError(error, 'Error loading products')
      } finally {
        this.loading = false
      }
    },

    async shareSingleProduct(product) {
      this.shareDialog  = true
      this.shareLoading = true
      this.shareData    = null
      try {
        const response = await axios.get(
          `/api/v1/manager/market_place/product/${product.id}/share-data/`
        )
        if (response.data.success) {
          const productData = response.data.data
          productData.selectedMedia = productData.has_video ? 'video' : 'image'
          this.shareData = [productData]
        } else {
          throw new Error(response.data.error)
        }
      } catch (error) {
        this.handleError(error, 'Error preparing share')
        this.shareDialog = false
      } finally {
        this.shareLoading = false
      }
    },

    async shareSelectedProducts() {
      if (this.selectedProducts.length === 0) {
        this.showSnackbar('Please select at least one product', 'warning')
        return
      }
      this.shareDialog  = true
      this.shareLoading = true
      this.shareData    = null
      try {
        const productIds = this.selectedProducts.map(p => p.id)
        const response   = await axios.post(
          '/api/v1/manager/market_place/products/share-data/',
          { product_ids: productIds }
        )
        if (response.data.success) {
          this.shareData = response.data.data.map(p => ({ ...p, selectedMedia: 'image' }))
        } else {
          throw new Error(response.data.error)
        }
      } catch (error) {
        this.handleError(error, 'Error preparing share')
        this.shareDialog = false
      } finally {
        this.shareLoading = false
      }
    },

    async openFacebookShare() {
      if (!this.shareData || !this.shareData.length) return

      const imageProducts = this.shareData.filter(p => !p.has_video || p.selectedMedia === 'image')
      const videoProducts = this.shareData.filter(p => p.has_video  && p.selectedMedia === 'video')

      imageProducts.forEach((product, index) => {
        const shareUrl    = `${product.backend_share_url}?media=image&t=${Date.now()}`
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
        setTimeout(() => {
          window.open(facebookUrl, `_blank_img_${index}`, 'width=600,height=500')
        }, index * 500)
      })

      if (videoProducts.length > 0) {
        this.shareLoading = true
        const results = await Promise.allSettled(
          videoProducts.map(product =>
            axios.post(`/api/v1/manager/market_place/product/${product.id}/share-video-facebook/`)
          )
        )
        this.shareLoading = false

        let successCount = 0
        const errors = []
        results.forEach((result, i) => {
          if (result.status === 'fulfilled' && result.value.data.success) {
            successCount++
            const postUrl = result.value.data.post_url
            if (postUrl) setTimeout(() => window.open(postUrl, `_blank_vid_${i}`, 'width=800,height=600'), i * 600)
          } else {
            const errMsg = result.value?.data?.error || result.reason?.response?.data?.error || 'Unknown error'
            errors.push(`${videoProducts[i].name}: ${errMsg}`)
          }
        })
        if (successCount > 0) this.showSnackbar(`✅ ${successCount} video(s) published on Facebook!`, 'success')
        errors.forEach(e => this.showSnackbar(`❌ ${e}`, 'error'))
      }

      const delay = Math.max(imageProducts.length * 500, 800)
      setTimeout(() => { this.shareDialog = false }, delay)
    },

    confirmDeleteProduct(product) {
      this.productToDelete = product
      this.deleteError     = null
      this.deleteDialog    = true
    },

    async deleteProduct() {
      if (!this.productToDelete) return
      this.deleting    = true
      this.deleteError = null
      try {
        const response = await axios.delete(
          `/api/v1/manager/market_place/delete/product/${this.productToDelete.id}/`
        )
        this.showSnackbar(response.data.message, 'success')
        this.deleteDialog = false
        const index = this.requestedProducts.findIndex(p => p.id === this.productToDelete.id)
        if (index !== -1) this.requestedProducts.splice(index, 1)
        this.productToDelete = null
        this.saveToStorage()
      } catch (error) {
        if (error.response) {
          if      (error.response.status === 403) this.deleteError = 'Permission denied. Only managers can delete products.'
          else if (error.response.status === 404) this.deleteError = 'Product not found. It may have been already deleted.'
          else this.deleteError = `Error: ${error.response.status} ${error.response.statusText}`
          this.handleError(error, null)
        } else {
          this.deleteError = 'Network error. Please check your connection and try again.'
          this.handleError(error, 'Network error')
        }
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.table-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Mobile card selected state */
.product-mobile-card {
  border: 1.5px solid #e0e0e0 !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.product-mobile-card.selected-card {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 2px #1976d220 !important;
}

.hover-primary:hover {
  color: #1976d2 !important;
  transition: color 0.2s ease;
}
.hover-scale { transition: transform 0.2s ease; }
.hover-scale:hover { transform: scale(1.15); }

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.facebook-preview { transition: transform 0.2s ease; }
.facebook-preview:hover { transform: translateY(-2px); }

::v-deep .v-data-table__wrapper tbody tr { transition: background-color 0.2s ease; }
::v-deep .v-data-table__wrapper tbody tr:hover { background-color: #f5f7fa !important; }
::v-deep .v-data-table-header th {
  font-size: 0.7rem !important;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
</style>